
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

export default function PrivacyPage() {
  const navigate = useNavigate();

  /**
   * Handles the “今すぐ予約” button click.
   * It navigates to the home page and, after a short delay,
   * scrolls the element with id="booking" into view.
   * Errors while navigating or scrolling are caught and logged
   * so the UI never crashes.
   */
  const handleBookingClick = () => {
    try {
      navigate('/');
      // Give the new page a moment to render before scrolling
      setTimeout(() => {
        const el = document.getElementById('booking');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        } else {
          console.warn('Element with id "booking" not found.');
        }
      }, 100);
    } catch (err) {
      console.error('Failed to navigate to home page:', err);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="text-2xl font-bold text-gray-800"
            style={{ fontFamily: "'Klee One', cursive" }}
          >
            いまのきもち
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">
              ホーム
            </Link>
            <Link to="/profile" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">
              自己紹介
            </Link>
            <Link to="/terms" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">
              注意事項
            </Link>
            <Link to="/faq" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">
              よくある質問
            </Link>
          </div>

          <button
            onClick={handleBookingClick}
            className="bg-[#FF9B8A] hover:bg-[#FF8A77] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
          >
            今すぐ予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF5F3] via-white to-[#F0F9FF]" />
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-[#A8D5E2]/10 rounded-full mb-6">
            <span className="text-[#A8D5E2] text-sm font-medium">個人情報の取り扱い</span>
          </div>

          <h1
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Klee One', cursive" }}
          >
            プライバシーポリシー
          </h1>

          <p className="text-lg text-gray-600">個人情報保護方針</p>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <div className="bg-gradient-to-br from-[#FFF5F3] to-white rounded-3xl p-8 md:p-10 border-2 border-[#FF9B8A]/20 mb-8">
            <p className="text-gray-700 leading-relaxed">
              いまのきもち（以下「当サービス」）は、ご利用者様の個人情報の保護を最優先事項と考え、以下のプライバシーポリシーに基づき、適切に取り扱います。
            </p>
          </div>

          {/* Section 1 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF9B8A] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">1</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">個人情報の定義</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    本プライバシーポリシーにおいて「個人情報」とは、個人情報保護法第2条第1項に定義される、生存する個人に関する情報であって、当該情報に含まれる氏名、生年月日、その他の記述等により特定の個人を識別できるもの、または個人識別符号が含まれるものを指します。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FFD93D] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">2</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">収集する個人情報</h2>
                <div className="text-gray-700 leading-relaxed space-y-4">
                  <p>当サービスでは、以下の個人情報を収集する場合があります：</p>

                  <div className="bg-[#FFFBF0] rounded-xl p-6 border border-[#FFD93D]/30">
                    <h3 className="font-bold text-gray-900 mb-3">ご利用者様本人の情報</h3>
                    <ul className="space-y-2 text-sm">
                      {[
                        '氏名（ニックネーム可）',
                        'メールアドレス',
                        '電話番号',
                        '年齢・生年月日',
                        '相談内容・セッション記録',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-[#FFD93D] rounded-full mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-[#F0F9FF] rounded-xl p-6 border border-[#A8D5E2]/30">
                    <h3 className="font-bold text-gray-900 mb-3">保護者の情報（18歳未満の方の場合）</h3>
                    <ul className="space-y-2 text-sm">
                      {['保護者の氏名', '保護者のメールアドレス', '保護者の電話番号', '続柄'].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-[#A8D5E2] rounded-full mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3">その他の情報</h3>
                    <ul className="space-y-2 text-sm">
                      {[
                        '決済情報（Stripeを通じて処理され、当サービスでは保存しません）',
                        'アクセスログ、IPアドレス、Cookie情報',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#A8D5E2] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">3</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">個人情報の利用目的</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>収集した個人情報は、以下の目的で利用します：</p>
                  <ul className="space-y-2 ml-6">
                    {[
                      'セッションの予約管理および実施',
                      'ご利用者様との連絡（予約確認、リマインド、変更・キャンセル対応）',
                      '決済処理および領収書の発行',
                      'サービス品質の向上および改善',
                      '緊急時の保護者または医療機関への連絡',
                      'お問い合わせへの対応',
                      '利用規約違反や不正利用の防止',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-[#A8D5E2] rounded-full mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF9B8A] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">4</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">個人情報の第三者提供</h2>
                <div className="text-gray-700 leading-relaxed space-y-4">
                  <p>
                    当サービスは、以下の場合を除き、ご本人の同意なく個人情報を第三者に提供することはありません。
                  </p>

                  <div className="bg-red-50 rounded-xl p-6 border border-red-200">
                    <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-alert-line text-red-600 text-lg w-4 h-4 flex items-center justify-center" />
                      </div>
                      例外的に第三者提供を行う場合
                    </h3>
                    <ul className="space-y-2 text-sm text-red-900">
                      {[
                        {
                          title: '生命・身体・財産の保護のため：',
                          desc:
                            'ご利用者様または第三者の生命、身体または財産の保護のために必要がある場合で、本人の同意を得ることが困難な場合（自傷・自殺の危険性が高い場合、虐待が疑われる場合など）',
                        },
                        {
                          title: '法令に基づく場合：',
                          desc: '法令に基づき開示が求められた場合',
                        },
                        {
                          title: '18歳未満の方の保護者への連絡：',
                          desc: '緊急時や必要と判断した場合、保護者の方へ連絡する場合',
                        },
                      ].map((item) => (
                        <li key={item.title} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-red-500 rounded-full mt-2 flex-shrink-0" />
                          <span>
                            <strong>{item.title}</strong> {item.desc}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-3">業務委託先への提供</h3>
                    <p className="text-sm mb-3">
                      サービス提供のため、以下の業務委託先に個人情報を提供する場合があります。この場合、適切な管理・監督を行います。
                    </p>
                    <ul className="space-y-2 text-sm">
                      {['決済代行サービス（Stripe）', 'メール配信サービス', 'データベース管理サービス（Supabase）'].map(
                        (item) => (
                          <li key={item} className="flex items-start gap-2">
                            <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FFD93D] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">5</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">個人情報の安全管理</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    当サービスは、個人情報の漏洩、滅失、毀損を防止するため、以下の安全管理措置を講じています：
                  </p>
                  <ul className="space-y-2 ml-6">
                    {['SSL/TLS暗号化通信の使用', 'アクセス権限の制限および管理', '定期的なセキュリティ対策の見直し', '個人情報の取り扱いに関する内部規程の整備'].map(
                      (item) => (
                        <li key={item} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-[#FFD93D] rounded-full mt-2 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      )
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#A8D5E2] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">6</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">個人情報の保存期間</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    個人情報は、利用目的を達成するために必要な期間のみ保存します。具体的には以下の通りです：
                  </p>
                  <ul className="space-y-2 ml-6">
                    {[
                      { label: '予約・連絡情報：', detail: 'セッション終了後1年間' },
                      {
                        label: 'セッション記録：',
                        detail: 'セッション終了後1年間（ただし、ご本人から削除依頼があった場合は速やかに削除します）',
                      },
                      { label: '決済情報：', detail: '法令に基づき必要な期間（通常5年間）' },
                    ].map((item) => (
                      <li key={item.label} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-[#A8D5E2] rounded-full mt-2 flex-shrink-0" />
                        <span>
                          <strong>{item.label}</strong> {item.detail}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 7 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF9B8A] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">7</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">個人情報の開示・訂正・削除</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>ご利用者様は、当サービスが保有する個人情報について、以下の権利を有します：</p>
                  <ul className="space-y-2 ml-6">
                    {['個人情報の開示請求', '個人情報の訂正・追加・削除請求', '個人情報の利用停止・消去請求'].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <div className="w-1.5 h-1.5 bg-[#FF9B8A] rounded-full mt-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4">
                    これらの請求を行う場合は、下記のお問い合わせ先までご連絡ください。ご本人確認の上、合理的な期間内に対応いたします。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 8 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FFD93D] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">8</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookie（クッキー）について</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    当サービスでは、サービスの利便性向上のため、Cookieを使用する場合があります。Cookieとは、Webサイトがお客様のコンピュータに送信する小さなテキストファイルです。
                  </p>
                  <p>
                    Cookieの使用を希望されない場合は、ブラウザの設定でCookieを無効にすることができます。ただし、Cookieを無効にした場合、一部のサービスが正常に機能しない可能性があります。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 9 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#A8D5E2] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">9</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">未成年者の個人情報</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    18歳未満の方がサービスをご利用される場合、保護者の同意が必要です。保護者の方の個人情報も収集し、緊急時や必要と判断した場合に連絡させていただくことがあります。
                  </p>
                  <p>未成年者の個人情報については、特に慎重に取り扱い、適切な保護措置を講じます。</p>
                </div>
              </div>
            </div>
          </div>

          {/* Section 10 */}
          <div className="mb-12">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-[#FF9B8A] rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-xl">10</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">プライバシーポリシーの変更</h2>
                <div className="text-gray-700 leading-relaxed space-y-3">
                  <p>
                    当サービスは、法令の変更や事業内容の変更等に伴い、本プライバシーポリシーを変更することがあります。変更後のプライバシーポリシーは、本ウェブサイトに掲載した時点から効力を生じるものとします。
                  </p>
                  <p>重要な変更がある場合は、ウェブサイト上で事前にお知らせいたします。</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gradient-to-br from-[#F0F9FF] to-white rounded-3xl p-8 md:p-10 border-2 border-[#A8D5E2]/30">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">お問い合わせ窓口</h2>
            <div className="text-gray-700 leading-relaxed space-y-4">
              <p>
                個人情報の取り扱いに関するお問い合わせ、開示・訂正・削除等のご請求は、以下の窓口までご連絡ください。
              </p>

              <div className="bg-white rounded-xl p-6 border border-[#A8D5E2]/30">
                <p className="font-bold text-gray-900 mb-3">いまのきもち 個人情報保護窓口</p>
                <div className="space-y-2 text-sm">
                  <p>
                    <strong>メールアドレス：</strong>info.within.com@gmail.com
                  </p>
                  <p className="text-gray-600">
                    ※ お問い合わせには、ご本人確認のため数日お時間をいただく場合があります。
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-8 text-center text-sm text-gray-500">
            <p>制定日：2024年1月1日</p>
            <p>最終更新日：2024年1月1日</p>
          </div>

          {/* Back to Home */}
          <div className="mt-12 text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#FF9B8A] hover:text-[#FF8A77] font-medium transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-left-line text-lg w-4 h-4 flex items-center justify-center" />
              </div>
              <span>ホームに戻る</span>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
